import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

const API_BASE_URL = process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL || "http://localhost:4009";

const FileUpload = ({ onUploadSuccess, onUploadError, maxFiles = 10 }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [failedFiles, setFailedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  const uploadSingleFile = async (file, retryCount = 0) => {
    const maxRetries = 3;
    console.log(`[FileUpload] Starting upload for file: ${file.name}`, {
      name: file.name,
      type: file.type,
      size: file.size,
      sizeMB: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
      sizeGB: (file.size / (1024 * 1024 * 1024)).toFixed(2) + ' GB',
      retryCount,
    });

    try {
      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: { 
          status: "uploading", 
          progress: 0,
          uploaded: 0,
          total: file.size,
          speed: 0,
          timeRemaining: 0,
        },
      }));

      const formData = new FormData();
      formData.append("file", file);
      console.log('[FileUpload] FormData created, sending to:', `${API_BASE_URL}/api/uploads`);

      const uploadStartTime = Date.now();
      
      // For large files, increase timeout (30 minutes for 5GB uploads)
      const isLargeFile = file.size > 100 * 1024 * 1024; // 100 MB
      const timeout = isLargeFile ? 30 * 60 * 1000 : 5 * 60 * 1000; // 30 min for large, 5 min for small
      
      console.log(`[FileUpload] Upload timeout set to: ${timeout / 1000 / 60} minutes (file size: ${(file.size / (1024 * 1024 * 1024)).toFixed(2)} GB)`);
      
      // Function to poll S3 upload progress
      const pollS3UploadProgress = async (uploadId, fileName, resolve, reject) => {
        const pollInterval = 1000; // Poll every 1 second
        const maxPolls = 1800; // Max 30 minutes (1800 seconds)
        let pollCount = 0;

        const poll = async () => {
          try {
            const response = await fetch(`${API_BASE_URL}/api/uploads/progress/${uploadId}`);
            const data = await response.json();

            if (data.success && data.data) {
              const progress = data.data;
              
              // Update progress with S3 upload progress (50-100% range)
              // progress.progress is 0-100 from S3, we map it to 50-100% of total
              const totalProgress = 50 + (progress.progress / 2); // 50% (server) + 50% (S3)
              
              setUploadProgress((prev) => ({
                ...prev,
                [fileName]: {
                  status: progress.status === 'completed' ? 'success' : progress.status,
                  progress: totalProgress,
                  uploaded: progress.uploaded,
                  total: progress.total,
                  speed: progress.speed,
                  timeRemaining: progress.timeRemaining,
                  message: progress.status === 'completed' ? 'Upload complete!' : 'Uploading to S3...',
                  uploadId: uploadId,
                },
              }));

              console.log(`[FileUpload] S3 upload progress: ${progress.progress.toFixed(2)}% (Total: ${totalProgress.toFixed(2)}%)`);

              if (progress.status === 'completed') {
                console.log('[FileUpload] S3 upload completed:', progress.result);
                setUploadProgress((prev) => ({
                  ...prev,
                  [fileName]: { status: "success", progress: 100 },
                }));
                resolve(progress.result);
                return; // Stop polling
              } else if (progress.status === 'error') {
                console.error('[FileUpload] S3 upload error:', progress.error);
                reject(new Error(progress.error || 'S3 upload failed'));
                return; // Stop polling
              }

              // Continue polling if still uploading
              pollCount++;
              if (pollCount < maxPolls) {
                setTimeout(poll, pollInterval);
              } else {
                reject(new Error('Upload timeout: S3 upload is taking too long'));
              }
            } else {
              console.error('[FileUpload] Failed to get progress:', data);
              reject(new Error(data.message || 'Failed to get upload progress'));
            }
          } catch (error) {
            console.error('[FileUpload] Error polling progress:', error);
            reject(new Error('Failed to check upload progress'));
          }
        };

        // Start polling
        setTimeout(poll, pollInterval);
      };
      
      // Use XMLHttpRequest for upload progress tracking (client to server)
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let lastLoaded = 0;
        let lastTime = Date.now();
        let uploadId = null;

        // Track upload progress from client to server
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            // Show progress for client-to-server upload (first 50% of total progress)
            const clientProgress = (e.loaded / e.total) * 50; // First half is client upload
            
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: {
                status: "uploading",
                progress: clientProgress,
                uploaded: e.loaded,
                total: e.total,
                speed: 0,
                timeRemaining: 0,
                message: "Uploading to server...",
              },
            }));

            console.log(`[FileUpload] Client upload progress: ${clientProgress.toFixed(2)}%`);
          }
        });

        // Handle completion of client-to-server upload
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              console.log('[FileUpload] Upload response data:', result);

              if (result.success && result.uploadId) {
                uploadId = result.uploadId;
                console.log('[FileUpload] File uploaded to server, uploadId:', uploadId);
                console.log('[FileUpload] Now tracking S3 upload progress...');
                
                // Update progress to show we're now uploading to S3
                setUploadProgress((prev) => ({
                  ...prev,
                  [file.name]: {
                    status: "uploading",
                    progress: 50, // 50% = file received by server
                    uploaded: file.size,
                    total: file.size,
                    speed: 0,
                    timeRemaining: 0,
                    message: "Uploading to S3...",
                    uploadId: uploadId,
                  },
                }));

                // Poll for S3 upload progress
                pollS3UploadProgress(uploadId, file.name, resolve, reject);
              } else {
                console.error('[FileUpload] Upload failed - result.success is false:', result);
                reject(new Error(result.message || `Failed to upload ${file.name}`));
              }
            } catch (parseError) {
              console.error('[FileUpload] Failed to parse response:', parseError);
              reject(new Error('Failed to parse server response'));
            }
          } else {
            console.error('[FileUpload] Upload failed with non-OK response:', {
              status: xhr.status,
              statusText: xhr.statusText,
              response: xhr.responseText,
            });
            reject(new Error(`HTTP ${xhr.status}: Failed to upload ${file.name}`));
          }
        });

        // Handle errors
        xhr.addEventListener('error', () => {
          console.error('[FileUpload] Upload network error');
          reject(new Error('Network error during upload'));
        });

        // Handle abort
        xhr.addEventListener('abort', () => {
          console.error('[FileUpload] Upload aborted');
          reject(new Error('Upload was aborted'));
        });

        // Set timeout
        xhr.timeout = timeout;
        xhr.addEventListener('timeout', () => {
          console.error('[FileUpload] Upload timeout');
          xhr.abort();
          reject(new Error('Upload timeout: The file is too large or upload is taking too long. Please try again.'));
        });

        // Open and send request
        xhr.open('POST', `${API_BASE_URL}/api/uploads`);
        xhr.send(formData);
      });
    } catch (err) {
      // Clear timeout if still active
      if (err.name === 'AbortError') {
        console.error('[FileUpload] Upload timeout - file too large or connection too slow');
        throw new Error(`Upload timeout: The file is too large or upload is taking too long. Please try again or use a smaller file.`);
      }
      console.error(`[FileUpload] Upload error for ${file.name}:`, {
        error: err.message,
        stack: err.stack,
        retryCount,
        maxRetries,
      });

      if (retryCount < maxRetries) {
        const retryDelay = 1000 * (retryCount + 1);
        console.log(`[FileUpload] Retrying upload for ${file.name} in ${retryDelay}ms (attempt ${retryCount + 1}/${maxRetries})`);
        // Retry with exponential backoff
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        return uploadSingleFile(file, retryCount + 1);
      } else {
        console.error(`[FileUpload] Max retries reached for ${file.name}. Upload failed.`);
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: { status: "error", progress: 0, error: err.message },
        }));
        setFailedFiles((prev) => [...prev, { file, error: err.message }]);
        throw err;
      }
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      console.log('[FileUpload] onDrop called:', {
        acceptedFilesCount: acceptedFiles.length,
        rejectedFilesCount: rejectedFiles.length,
        acceptedFiles: acceptedFiles.map(f => ({ name: f.name, type: f.type, size: f.size })),
        rejectedFiles: rejectedFiles.map(({ file, errors }) => ({ name: file.name, errors })),
      });

      if (acceptedFiles.length === 0 && rejectedFiles.length === 0) {
        console.log('[FileUpload] No files to process');
        return;
      }

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        console.warn('[FileUpload] Files rejected:', rejectedFiles);
        const rejectionErrors = rejectedFiles.map(({ file, errors }) => {
          const errorMessages = errors.map((e) => {
            if (e.code === "file-too-large") {
              // Use the custom message from validator
              return e.message || "File size exceeds limit";
            }
            if (e.code === "file-invalid-type") {
              return "Invalid file type. Only images (JPG, PNG, WEBP) and videos (MP4, WEBM, MOV, AVI, MKV, 3GP, FLV, WMV, MPEG, MPG, M4V, OGV) are allowed";
            }
            return e.message;
          });
          return `${file.name}: ${errorMessages.join(", ")}`;
        });
        setError(rejectionErrors.join("; "));
      }

      if (acceptedFiles.length === 0) {
        console.log('[FileUpload] No accepted files to upload');
        return;
      }

      console.log('[FileUpload] Starting upload process for', acceptedFiles.length, 'file(s)');
      setError(null);
      setFailedFiles([]);
      setUploadProgress({});
      setUploading(true);

      try {
        const uploadPromises = acceptedFiles.map((file) =>
          uploadSingleFile(file)
        );

        console.log('[FileUpload] All upload promises created, waiting for results...');
        const results = await Promise.allSettled(uploadPromises);
        const successful = results
          .filter((r) => r.status === "fulfilled")
          .map((r) => r.value);
        const failed = results.filter((r) => r.status === "rejected");

        console.log('[FileUpload] Upload results:', {
          total: results.length,
          successful: successful.length,
          failed: failed.length,
          successfulFiles: successful.map(s => s?.filename),
          failedFiles: failed.map(f => f.reason?.message),
        });

        setUploading(false);

        if (successful.length > 0) {
          console.log('[FileUpload] Calling onUploadSuccess with', successful.length, 'file(s)');
          onUploadSuccess(successful);
        }

        if (failed.length > 0) {
          const errorMessages = failed.map((f) => f.reason?.message || "Upload failed");
          console.error('[FileUpload] Some uploads failed:', errorMessages);
          setError(`Failed to upload ${failed.length} file(s): ${errorMessages.join("; ")}`);
          if (onUploadError) {
            onUploadError(new Error(errorMessages.join("; ")));
          }
        }
      } catch (err) {
        console.error('[FileUpload] Unexpected error during upload process:', err);
        setUploading(false);
        const errorMessage = err.message || "Failed to upload files";
        setError(errorMessage);
        if (onUploadError) {
          onUploadError(err);
        }
      }
    },
    [onUploadSuccess, onUploadError]
  );

  const handleRetryFailedFiles = async () => {
    if (failedFiles.length === 0) return;

    setError(null);
    setUploading(true);
    const filesToRetry = failedFiles.map((f) => f.file);
    setFailedFiles([]);

    try {
      const uploadPromises = filesToRetry.map((file) => uploadSingleFile(file));
      const results = await Promise.allSettled(uploadPromises);
      const successful = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value);
      const failed = results.filter((r) => r.status === "rejected");

      setUploading(false);

      if (successful.length > 0) {
        onUploadSuccess(successful);
      }

      if (failed.length > 0) {
        const errorMessages = failed.map((f) => f.reason?.message || "Upload failed");
        setError(`Failed to upload ${failed.length} file(s): ${errorMessages.join("; ")}`);
        setFailedFiles(
          failed.map((f, i) => ({
            file: filesToRetry[i],
            error: f.reason?.message || "Upload failed",
          }))
        );
      }
    } catch (err) {
      setUploading(false);
      setError(err.message || "Failed to retry uploads");
    }
  };

  // Custom validator function to check file size based on file type
  const fileSizeValidator = (file) => {
    console.log('[FileUpload] Validating file:', {
      name: file.name,
      type: file.type,
      size: file.size,
      sizeMB: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
      sizeGB: (file.size / (1024 * 1024 * 1024)).toFixed(2) + ' GB',
    });

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    
    console.log('[FileUpload] File type detection:', { isImage, isVideo });
    
    if (isImage) {
      const maxImageSize = 20 * 1024 * 1024; // 20 MB for images
      console.log('[FileUpload] Image validation - Max size: 20 MB, File size:', (file.size / (1024 * 1024)).toFixed(2) + ' MB');
      if (file.size > maxImageSize) {
        console.error('[FileUpload] Image size validation FAILED:', {
          fileSize: file.size,
          maxSize: maxImageSize,
          exceededBy: ((file.size - maxImageSize) / (1024 * 1024)).toFixed(2) + ' MB',
        });
        return {
          code: "file-too-large",
          message: `Image size exceeds 20 MB. Maximum allowed size for images is 20 MB.`,
        };
      }
      console.log('[FileUpload] Image size validation PASSED');
    } else if (isVideo) {
      const maxVideoSize = 5 * 1024 * 1024 * 1024; // 5 GB for videos
      console.log('[FileUpload] Video validation - Max size: 5 GB, File size:', (file.size / (1024 * 1024 * 1024)).toFixed(2) + ' GB');
      if (file.size > maxVideoSize) {
        console.error('[FileUpload] Video size validation FAILED:', {
          fileSize: file.size,
          fileSizeGB: (file.size / (1024 * 1024 * 1024)).toFixed(2) + ' GB',
          maxSize: maxVideoSize,
          maxSizeGB: '5 GB',
          exceededBy: ((file.size - maxVideoSize) / (1024 * 1024 * 1024)).toFixed(2) + ' GB',
        });
        return {
          code: "file-too-large",
          message: `Video size exceeds 5 GB. Maximum allowed size for videos is 5 GB.`,
        };
      }
      console.log('[FileUpload] Video size validation PASSED');
    }
    return null;
  };

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".webp"],
      "video/*": [
        ".mp4", 
        ".webm", 
        ".mov", 
        ".avi", 
        ".mkv", 
        ".3gp", 
        ".flv", 
        ".wmv", 
        ".mpeg", 
        ".mpg",
        ".m4v",
        ".ogv"
      ],
    },
    validator: fileSizeValidator,
    multiple: true,
    maxFiles: maxFiles,
    onDropRejected: (rejectedFiles) => {
      const rejectionErrors = rejectedFiles.map(({ file, errors }) => {
        const errorMessages = errors.map((e) => {
          if (e.code === "file-too-large") {
            // Use the custom message from validator
            return e.message || "File size exceeds limit";
          }
          if (e.code === "file-invalid-type") {
            return "Invalid file type. Only images (JPG, PNG, WEBP) and videos (MP4, WEBM, MOV, AVI, MKV, 3GP, FLV, WMV, MPEG, MPG, M4V, OGV) are allowed";
          }
          if (e.code === "too-many-files") {
            return `Maximum ${maxFiles} files allowed`;
          }
          return e.message;
        });
        return `${file.name}: ${errorMessages.join(", ")}`;
      });
      setError(rejectionErrors.join("; "));
    },
  });

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: `2px dashed ${isDragActive ? "#9048FB" : "#e5e7eb"}`,
          borderRadius: "12px",
          padding: 3,
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isDragActive ? "#f3e8ff" : "#fafafa",
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: "#9048FB",
            backgroundColor: "#f8fafc",
          },
        }}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, width: "100%" }}>
            <CircularProgress sx={{ color: "#9048FB" }} />
            <Typography sx={{ fontFamily: "Outfit", color: "#6b7280", fontWeight: 600 }}>
              Uploading files...
            </Typography>
            
            {/* Show progress for each file being uploaded */}
            {Object.entries(uploadProgress).map(([fileName, progress]) => {
              if (progress.status === "uploading" || progress.status === "success") {
                const uploadedMB = (progress.uploaded / (1024 * 1024)).toFixed(2);
                const totalMB = (progress.total / (1024 * 1024)).toFixed(2);
                const totalGB = (progress.total / (1024 * 1024 * 1024)).toFixed(2);
                const speedMB = progress.speed > 0 ? (progress.speed / (1024 * 1024)).toFixed(2) : 0;
                const timeRemainingMin = Math.floor(progress.timeRemaining / 60);
                const timeRemainingSec = Math.floor(progress.timeRemaining % 60);
                
                return (
                  <Box key={fileName} sx={{ width: "100%", mt: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "12px",
                          color: "#374151",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                      >
                        {fileName}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "12px",
                          color: progress.status === "success" ? "#10b981" : "#9048FB",
                          fontWeight: 600,
                          ml: 1,
                        }}
                      >
                        {progress.progress.toFixed(1)}%
                      </Typography>
                    </Box>
                    {progress.message && (
                      <Typography
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "11px",
                          color: "#6b7280",
                          mb: 0.5,
                          fontStyle: "italic",
                        }}
                      >
                        {progress.message}
                      </Typography>
                    )}
                    <LinearProgress
                      variant="determinate"
                      value={progress.progress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "#e5e7eb",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: progress.status === "success" ? "#10b981" : "#9048FB",
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "11px",
                          color: "#6b7280",
                        }}
                      >
                        {uploadedMB} MB / {totalGB > 1 ? `${totalGB} GB` : `${totalMB} MB`}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        {speedMB > 0 && progress.status === "uploading" && (
                          <Typography
                            sx={{
                              fontFamily: "Outfit",
                              fontSize: "11px",
                              color: "#6b7280",
                            }}
                          >
                            {speedMB} MB/s
                          </Typography>
                        )}
                        {progress.timeRemaining > 0 && progress.status === "uploading" && (
                          <Typography
                            sx={{
                              fontFamily: "Outfit",
                              fontSize: "11px",
                              color: "#6b7280",
                            }}
                          >
                            {timeRemainingMin > 0 ? `${timeRemainingMin}m ` : ''}{timeRemainingSec}s remaining
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              }
              return null;
            })}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CloudUploadIcon
              sx={{
                fontSize: 48,
                color: isDragActive ? "#9048FB" : "#9ca3af",
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  color: "#374151",
                  mb: 0.5,
                }}
              >
                {isDragActive
                  ? "Drop files here"
                  : "Drag & drop files here, or click to select"}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontSize: "12px",
                  color: "#6b7280",
                }}
              >
                Images (JPG, PNG, WEBP) up to 20 MB • Videos (MP4, WEBM, MOV, AVI, MKV, 3GP, FLV, WMV, MPEG, MPG, M4V, OGV) up to 5 GB
              </Typography>
            </Box>
            <Button
              variant="outlined"
              sx={{
                borderColor: "#9048FB",
                color: "#9048FB",
                textTransform: "none",
                fontFamily: "Outfit",
                "&:hover": {
                  borderColor: "#7c3aed",
                  backgroundColor: "#f3e8ff",
                },
              }}
            >
              Select Files
            </Button>
          </Box>
        )}
      </Box>
      {error && (
        <Alert
          severity="error"
          sx={{ mt: 2, fontFamily: "Outfit" }}
          action={
            failedFiles.length > 0 && (
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRetryFailedFiles}
                disabled={uploading}
                sx={{
                  fontFamily: "Outfit",
                  textTransform: "none",
                  color: "#ffffff",
                }}
              >
                Retry
              </Button>
            )
          }
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export const AttachmentPreview = ({
  attachment,
  onRemove,
  onDragStart,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onDragEnd,
  isDragging,
  isDragOver,
  index,
}) => {
  const isImage = attachment.type === "image";
  const isVideo = attachment.type === "video";

  return (
    <Box
      draggable
      onDragStart={(e) => onDragStart && onDragStart(e, index)}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver && onDragOver(e, index);
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        onDragEnter && onDragEnter(e, index);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        onDragLeave && onDragLeave(e);
      }}
      onDrop={(e) => onDrop && onDrop(e, index)}
      onDragEnd={(e) => onDragEnd && onDragEnd()}
      sx={{
        position: "relative",
        border: `2px solid ${
          isDragging
            ? "#9048FB"
            : isDragOver
            ? "#7c3aed"
            : "#e5e7eb"
        }`,
        borderRadius: "8px",
        overflow: "hidden",
        cursor: "move",
        transition: "all 0.2s ease",
        opacity: isDragging ? 0.5 : 1,
        transform: isDragging ? "scale(0.95)" : "scale(1)",
        backgroundColor:
          isDragOver && !isDragging ? "#f3e8ff" : "transparent",
        boxShadow:
          isDragging || isDragOver
            ? "0 4px 12px rgba(144, 72, 251, 0.3)"
            : "0 1px 3px rgba(0, 0, 0, 0.1)",
        "&:hover": {
          borderColor: "#9048FB",
          boxShadow: "0 2px 8px rgba(144, 72, 251, 0.2)",
        },
      }}
    >
      {isImage && (
        <Box
          component="img"
          src={attachment.url}
          alt={attachment.filename}
          sx={{
            width: "100%",
            height: "120px",
            objectFit: "cover",
            display: "block",
          }}
        />
      )}
      {isVideo && (
        <Box
          sx={{
            width: "100%",
            height: "120px",
            backgroundColor: "#1f2937",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <VideoIcon sx={{ fontSize: 40, color: "#ffffff" }} />
          <Box
            component="video"
            src={attachment.url}
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: 0.3,
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          padding: 0.5,
        }}
      >
        <IconButton
          size="small"
          onClick={() => onRemove && onRemove(attachment.id)}
          sx={{
            color: "#ffffff",
            padding: 0.5,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          padding: 0.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Outfit",
            fontSize: "10px",
            color: "#ffffff",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {attachment.filename}
        </Typography>
      </Box>
    </Box>
  );
};

export default FileUpload;

